import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { DataLabel, DataValue, daysUntil, urgencyClass, fmtDate } from "@/lib/utils-ui";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from "recharts";
import { Button } from "@/components/ui/button";
import { Bell, Zap } from "lucide-react";
import { toast } from "sonner";

const STATUS_ORDER = ["open","assigned","in_progress","resolved","closed"];
const COLORS = ["#FF5000","#FFB020","#1F6FEB","#888888","#111111"];

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [integrations, setIntegrations] = useState(null);
  const [scanning, setScanning] = useState(false);

  const loadStats = () => api.get("/dashboard/stats").then(r => setStats(r.data)).catch(() => {});

  useEffect(() => {
    loadStats();
    if (["super_admin","company_admin"].includes(user?.role)) {
      api.get("/admin/integrations/status").then(r => setIntegrations(r.data)).catch(() => {});
    }
  }, [user?.role]);

  const runAlerts = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/admin/alerts/run-now");
      toast.success(`Scan complete · ${data.created} new alerts · ${data.emails_sent} emails · ${data.teams_sent} Teams`);
      loadStats();
    } catch { toast.error("Scan failed"); }
    finally { setScanning(false); }
  };

  if (!stats) return <div className="p-10 data-label">Loading command center…</div>;

  const kpis = [
    { label: "Total Assets", value: stats.total_assets, sub: `${stats.active_assets} active / ${stats.repair_assets} repair`, testid: "kpi-assets" },
    { label: "Open Tickets", value: stats.open_tickets, sub: `${stats.critical_tickets} critical`, testid: "kpi-tickets" },
    { label: "Expiring ≤30d", value: stats.expiring_30, sub: `${stats.expiring_7} within 7 days`, testid: "kpi-expiring", accent: stats.expiring_7 > 0 },
    { label: "Audits on File", value: stats.audits_total, sub: `${stats.vendors_total} vendors`, testid: "kpi-audits" },
  ];

  const statusData = STATUS_ORDER.map(s => ({ status: s.replace("_"," "), count: stats.ticket_status_dist[s] || 0 }));
  const assetData = Object.entries(stats.asset_type_dist).map(([k, v]) => ({ name: k, value: v }));

  return (
    <div className="p-8 space-y-8" data-testid="dashboard-page">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="data-label">Welcome · {new Date().toDateString()}</div>
          <h1 className="font-head font-black uppercase text-4xl lg:text-5xl tracking-tighter mt-1">
            Good day, <span className="text-[#FF5000]">{user?.full_name?.split(" ")[0]}</span>.
          </h1>
        </div>
        {["super_admin","company_admin","it_admin"].includes(user?.role) && (
          <div className="flex items-center gap-2" data-testid="admin-actions">
            {integrations && (
              <div className="flex items-center gap-2 border border-border bg-white px-3 py-2 text-[10px] uppercase tracking-wider" data-testid="integrations-status">
                <span className="flex items-center gap-1">
                  <span className={`w-1.5 h-1.5 inline-block ${integrations.email ? "bg-[#00C06A]" : "bg-muted-foreground"}`}></span>
                  SMTP
                </span>
                <span className="flex items-center gap-1">
                  <span className={`w-1.5 h-1.5 inline-block ${integrations.teams ? "bg-[#00C06A]" : "bg-muted-foreground"}`}></span>
                  TEAMS
                </span>
              </div>
            )}
            <Button data-testid="run-alerts-btn" onClick={runAlerts} disabled={scanning}
                    className="bg-black text-white hover:bg-[#FF5000] h-10 uppercase tracking-wider text-xs font-head">
              <Zap size={14} className="mr-1"/>{scanning ? "Scanning…" : "Run Alert Scan"}
            </Button>
          </div>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border border-border" data-testid="kpi-row">
        {kpis.map((k, i) => (
          <div key={k.label} data-testid={k.testid} className={`p-6 border-border ${i < kpis.length - 1 ? "md:border-r" : ""} ${k.accent ? "bg-[#FFF4EB]" : "bg-white"}`}>
            <DataLabel>{k.label}</DataLabel>
            <div className="font-head font-black text-5xl tracking-tighter mt-2">{k.value}</div>
            <div className="text-xs text-muted-foreground mt-1">{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 border border-border bg-white">
        <div className="p-6 border-r border-border lg:col-span-2">
          <div className="flex items-baseline justify-between">
            <div>
              <DataLabel>Tickets · Last 7 Days</DataLabel>
              <h3 className="font-head font-bold text-lg mt-1">Incoming volume</h3>
            </div>
          </div>
          <div className="h-64 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.tickets_trend_7d}>
                <XAxis dataKey="date" stroke="#999" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} />
                <YAxis stroke="#999" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} />
                <Tooltip contentStyle={{ borderRadius: 0, border: "1px solid #ccc", fontFamily: "JetBrains Mono", fontSize: 12 }} />
                <Bar dataKey="count" fill="#FF5000" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="p-6">
          <DataLabel>Ticket Status</DataLabel>
          <h3 className="font-head font-bold text-lg mt-1">Pipeline</h3>
          <div className="h-64 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={statusData} dataKey="count" nameKey="status" innerRadius={40} outerRadius={80} paddingAngle={1}>
                  {statusData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 0, fontFamily: "JetBrains Mono", fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Upcoming expirations + asset breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 border border-border bg-white">
        <div className="p-6 lg:col-span-2 border-r border-border">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <DataLabel>Upcoming Expirations</DataLabel>
              <h3 className="font-head font-bold text-lg mt-1">Renew within 30 days</h3>
            </div>
          </div>
          {stats.upcoming_subscriptions.length === 0 && <div className="text-xs text-muted-foreground">Nothing expiring soon. Steady state.</div>}
          <div className="divide-y divide-border">
            {stats.upcoming_subscriptions.map(s => {
              const d = daysUntil(s.expiry_date);
              return (
                <div key={s.id} className="py-3 flex items-center justify-between text-sm" data-testid={`upcoming-sub-${s.id}`}>
                  <div>
                    <div className="font-semibold">{s.name}</div>
                    <div className="text-xs text-muted-foreground font-mono">{s.provider} · {s.type}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-xs">{fmtDate(s.expiry_date)}</div>
                    <div className={`data-label ${urgencyClass(d)}`}>{d} DAYS</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="p-6">
          <DataLabel>Asset Mix</DataLabel>
          <h3 className="font-head font-bold text-lg mt-1">By type</h3>
          <div className="mt-4 space-y-2">
            {assetData.map(a => (
              <div key={a.name} className="flex items-center justify-between text-xs font-mono">
                <span className="uppercase">{a.name}</span>
                <span>{a.value}</span>
              </div>
            ))}
            {assetData.length === 0 && <div className="text-xs text-muted-foreground">No assets yet.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
